// Create a new file: /components/workflow/StructuralConditionalNodeModal.jsx

import React, { useState, useMemo } from 'react';
import { Link2, Network } from 'lucide-react';
import SchemaViewer from '../NewWorkflow/shared/SchemaViewer'; // Adjust path if needed
import { fieldsToSchema, schemaToFields, flattenSchemaToFields } from '../NewWorkflow/utils/schemaUtils';

const StructuralConditionalNodeModal = ({ mode, node, nodes, edges, onSave, onDelete, onClose, globalState }) => {
    const [selectedConditionPath, setSelectedConditionPath] = useState(node.data.selectedConditionPath || null);


    const inputRootFields = useMemo(() => {
        const connectedInputNodes = edges
            .filter(edge => edge.target === node.id)
            .map(edge => nodes.find(n => n.id === edge.source))
            .filter(Boolean)
            .filter(parentNode => parentNode.type !== 'loopBreak') // ⭐ ADD THIS LINE
            .map(parentNode => {
                let schema;
                if (parentNode.data.jsonOutput && parentNode.data.jsonOutputSchema?.properties) {
                    schema = parentNode.data.jsonOutputSchema;
                } else {
                    schema = {
                        title: 'Simple Output', type: 'object',
                        properties: { 'output': { type: 'string', description: `Output from "${parentNode.data.label || parentNode.type}"` } }
                    };
                }
                return {
                    id: `input_${parentNode.id}`, fieldName: parentNode.data.label || 'Input Node',
                    dataType: 'Object', children: schemaToFields(schema, ''),
                };
            }).filter(Boolean);

        // ⭐ 2. Create a "virtual node" for the Global State if it exists
        const globalStateNode = [];
        if (globalState && globalState.fields.length > 0) {
            const globalSchema = fieldsToSchema(globalState.fields, "globalState");
            globalStateNode.push({
                id: 'globalState', // Special, reserved ID
                fieldName: 'Global State',
                dataType: 'Object',
                children: schemaToFields(globalSchema, ''),
            });
        }

        // ⭐ 3. Combine the Global State with the other input nodes
        return [...globalStateNode, ...connectedInputNodes];
    }, [node.id, nodes, edges]);

    const allInputFields = useMemo(() => flattenSchemaToFields(inputRootFields), [inputRootFields]);

    const handleFieldClick = (path, field) => {
        setSelectedConditionPath(path);
    };

    // This function tells the SchemaViewer to only allow Boolean fields to be clickable
    const isFieldValid = (field) => field.dataType.toLowerCase() === 'boolean';

    // Auto-generate the output schema
    const { jsonOutputSchema, outputRootField } = useMemo(() => {
        const outputFields = [{
            id: 'condition',
            fieldName: 'condition',
            dataType: 'Boolean',
            children: [],
        }];
        const schema = fieldsToSchema(outputFields);
        const rootFieldForViewer = [{
            id: 'root', fieldName: 'Output', dataType: 'Object', children: outputFields
        }];
        return { jsonOutputSchema: schema, outputRootField: rootFieldForViewer };
    }, []);

    const handleSaveAndClose = () => {
        onSave({
            ...node,
            data: {
                ...node.data,
                label: `If: ${selectedConditionPath?.split('.').pop() || ''}`,
                selectedConditionPath,
                jsonOutputSchema, // The simple { "condition": boolean } schema
                jsonOutput: true,
            },
        });
        onClose();
    };

    // --- NEW: Find the display name for the selected path ---
    const selectedFieldDisplayName = useMemo(() => {
        if (!selectedConditionPath) return null;
        const field = allInputFields.find(f => f.path === selectedConditionPath);
        return field ? field.display : selectedConditionPath;
    }, [selectedConditionPath, allInputFields]);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
            <div className="bg-slate-800 w-full max-w-7xl h-[90vh] rounded-xl shadow-2xl border border-slate-700 flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-700 flex-shrink-0">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                        <Network /> Configure Structural Condition Node
                    </h2>
                </div>
                <div className="flex-grow grid grid-cols-2 gap-4 p-4 overflow-hidden">
                    <div className="flex flex-col gap-2 overflow-y-auto pr-2">
                        <h3 className="text-lg font-semibold text-slate-200">Select a Boolean Field</h3>
                        <p className="text-sm text-slate-400">Only boolean fields are enabled. The workflow will branch based on this field's value.</p>
                        <SchemaViewer
                            rootFields={inputRootFields}
                            onFieldClick={handleFieldClick}
                            isValidField={isFieldValid}
                            selectedPath={selectedConditionPath}
                        />
                    </div>
                    <div className="flex flex-col gap-2 overflow-y-auto pl-2">
                        <h3 className="text-lg font-semibold text-slate-200">Generated Output</h3>
                        <p className="text-sm text-slate-400">For logging, the node will output an object with the resolved boolean value.</p>
                        <SchemaViewer rootFields={outputRootField} />
                        {/* --- NEW: VISUAL MAPPING INDICATOR --- */}
                        {selectedFieldDisplayName && (
                            <div className="mt-4 p-4 bg-zinc-900/50 border border-zinc-700 rounded-lg">
                                <h4 className="text-sm font-semibold text-slate-200 mb-2">Mapping</h4>
                                <div className="flex items-center gap-3 text-sm">
                                    <div className="flex items-center gap-2 text-indigo-300 font-mono truncate bg-indigo-500/10 px-2 py-1 rounded-md">
                                        <Link2 size={14} />
                                        <span>{selectedFieldDisplayName}</span>
                                    </div>
                                    <span className="text-slate-400">→</span>
                                    <div className="font-mono text-slate-100 bg-slate-700 px-2 py-1 rounded-md">
                                        condition
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-4 flex justify-between items-center bg-zinc-800/50 border-t border-zinc-700 rounded-b-lg flex-shrink-0">
                    {mode === 'edit' && node?.id && (
                        <button onClick={() => onDelete(node.id)} className="px-4 py-2 text-sm font-semibold text-red-400 bg-red-900/50 hover:bg-red-900/80 rounded-md transition-colors">
                            Delete Node
                        </button>
                    )}
                    <div className="flex-grow" />
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-300 bg-zinc-700 hover:bg-zinc-600 rounded-md transition-colors">Cancel</button>
                        <button onClick={handleSaveAndClose} className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-md transition-colors">Save Changes</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StructuralConditionalNodeModal;